import { CentricTypography as Typography } from '@core/component/CentricTypography'
import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell'
import useOrderContext from '@modules/order/hooks/use-order-context'
import useViewContext from '@modules/order/hooks/use-view-context'
import formatNumberByLocale from '@utils/format-number-by-locale'
import isNumber from 'lodash/isNumber'
import { useObserver } from 'mobx-react-lite'
import React, { FC, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { nav } from '@stores/nav'
import { makeStyles } from '@material-ui/core'
import { EnhancedListUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/EnhancedListUIStore'
import { useEnhancedBookQuantity } from './useEnhancedBookQuantity'
import EnhancedProductSizesList from './EnhancedProductSizesList'
import EnhancedBookQtyTotals from './EnhancedBookQtyTotals'
import EnhancedProductQuantitiesUIStore from '@modules/showroom/productDetail/EnhancedProductQuantities/stores/EnhancedProductQuantitiesUIStore'
import useHierarchyItemTableContext from '@components/UI-Components/HierarchyWiseItemsTable/hooks/use-hierarchy-item-table-context'
import EnhancedBookQtyCheckbox from './EnhancedBookQtyCheckbox'
import { isRetailClient } from '@utils/get-channel-name'

type EnhancedBookQtyProps = CellItem & {
  isProductDetailsView?: boolean
}

const useStyles = makeStyles(theme => ({
  bookQty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: 'max-content'
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 0',
    width: '150px',
    justifyContent: 'center'
  },
  totalLabel: {
    paddingBottom: 2
  },
  totalValue: {
    margin: 5
  },
  bookUnbookAllWrapper: {
    height: 53,
    width: 150
  },
  sizeContainer: {
    padding: 10,
    width: '150px',
    height: 77,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  sizes: {
    width: 150,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  singleSize: {
    height: 32,
    display: 'flex',
    alignItems: 'center'
  },
  inactive: {
    opacity: '0.5'
  },
  showHideSizesBtn: {
    cursor: 'pointer'
  }
}))

/**
 * Product-row "totals" column with book/unbook-all checkbox and product sizes
 */
const EnhancedBookQty: FC<EnhancedBookQtyProps> = ({ rowData, isProductDetailsView }) =>
  useObserver(function useHook() {
    const { t } = useTranslation()
    const viewContextVal = useViewContext()

    const {
      queryParams: { bspId, clientType }
    } = nav

    const productDetailsContextVal = useHierarchyItemTableContext()
    const store = isProductDetailsView
      ? (productDetailsContextVal.store as EnhancedProductQuantitiesUIStore)
      : (viewContextVal.uiStore as EnhancedListUIStore<any>)

    const {
      hideShowProductSizes,
      showProductSizes,
      getSizes,
      getBookedDoors,
      dataStore: { itemIndexObjectStore },
      isValueSelected
    } = store

    const {
      uiStore: {
        dataStore: { summary },
        isOrderEditable
      }
    } = useOrderContext()
    const classes = useStyles()
    const isRetail = isRetailClient(clientType)

    const {
      rowDataId,
      bookedCells,
      captureSizeEnabled,
      sizes,
      isProductActive,
      isAnyBookUnbookInProgress,
      isBookingAllInProgress,
      isUnBookingAllInProgress,
      areAllDoorsBooked,
      statusChangeHandler
    } = useEnhancedBookQuantity({
      rowData,
      orderType: nav.queryParams.orderType,
      summary,
      getBookedDoors,
      isValueSelected,
      isRetail,
      itemIndexObjectStore,
      bookUnbookDoors: store.bookUnbookDoors
    })

    if (!rowData) return <></>

    return (
      <div className={classes.bookQty} key={rowData.id} data-test-id='EnhancedBookQty'>
        <div className={classes.bookUnbookAllWrapper}>
          {!summary?.doorOrder && (
            <div className={classes.checkboxContainer}>
              <Typography variant={'body2'}>
                {t('orderTable.cnfBookOrUnBookAll', 'Book / Un-Book All')}
              </Typography>
              <EnhancedBookQtyCheckbox
                isOrderEditable={isOrderEditable}
                isProductActive={isProductActive}
                isAnyBookUnbookInProgress={isAnyBookUnbookInProgress}
                isBookUnbookAllInProgress={
                  isBookingAllInProgress || isUnBookingAllInProgress
                }
                areAllDoorsBooked={areAllDoorsBooked}
                statusChangeHandler={statusChangeHandler}
              />
            </div>
          )}
        </div>
        <EnhancedBookQtyTotals
          isReset={isUnBookingAllInProgress}
          bookedCells={bookedCells}
        />
        {sizes?.length && captureSizeEnabled ? (
          <div className={classes.sizeContainer}>
            <div className={classes.totalLabel}>
              <Typography variant={'body2'}>{bookedCells.cell2.cellLabel}</Typography>
            </div>
            {(isNumber(bookedCells?.cell2?.cellValue) || true) && (
              <div className={classes.totalValue}>
                <Typography variant={'h6DarkGrey'}>
                  {`${bookedCells.cell2.cellCurrency}${formatNumberByLocale(
                    bookedCells.cell2.cellValue
                  )}`}
                </Typography>
              </div>
            )}
            {!isProductDetailsView && (
              <div
                onClick={() => {
                  hideShowProductSizes(!showProductSizes[rowData?.id], rowData?.id)
                }}
                className={classes.showHideSizesBtn}
              >
                <Typography variant='body2Blue'>
                  {showProductSizes[rowData?.id]
                    ? `- ${t('orderTable.hideProductSizes', 'Hide Product Sizes')}`
                    : `+ ${t('orderTable.showProductSizes', 'Show Product Sizes')}`}
                </Typography>
              </div>
            )}
          </div>
        ) : null}
        <div className={classes.sizes}>
          <EnhancedProductSizesList
            rowDataId={rowDataId}
            showProductSizes={
              isProductDetailsView
                ? (showProductSizes[rowData?.id] || bspId) && captureSizeEnabled
                : showProductSizes[rowDataId]
            }
            getSizes={getSizes}
          />
        </div>
      </div>
    )
  })

export default memo(EnhancedBookQty)
